import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  TableHead,
  Box,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import { grey } from '@mui/material/colors';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';

// Using a similar styled row for hover effects and selection indication
import { styled } from '@mui/material/styles';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& > td': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

function CustomObjectExpandableTable({ data, onDelete }) {
  const [expandedRow, setExpandedRow] = useState(null);

  const handleRowClick = (groupName) => {
    setExpandedRow((prevExpandedRow) => (prevExpandedRow === groupName ? null : groupName));
  };

  const renderRowSubComponent = (group) => {
    const subColumns = [
      { name: 'version', label: 'Version', align: 'left' },
      { name: 'createdDate', label: 'Created Date', align: 'left' },
      { name: 'createdBy', label: 'Created By', align: 'left' },
      { name: 'actions', label: 'Actions', align: 'right' },
    ];

    return (
      <TableRow>
        <TableCell colSpan={3} style={{ padding: 0, border: 'none' }}>
          <Collapse in={expandedRow === group.name} timeout="auto" unmountOnExit>
            <ArgonBox p={2} bgcolor={grey[50]}>
              <TableContainer component={Paper} elevation={0} variant="outlined">
                <Table size="small">
                  <TableHead sx={{ display: 'contents' }}>
                    <TableRow>
                      {subColumns.map((column) => (
                        <TableCell key={column.name} align={column.align} sx={{ fontWeight: 'bold', color: grey[700] }}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.versions.map((version) => (
                      <TableRow key={version._id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>
                          <ArgonTypography variant="caption" color="secondary">
                            {version.version} {version.is_latest && '(Latest)'}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption" color="secondary">
                            {new Date(version.created_at).toLocaleString()}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption" color="secondary">
                            {version.user?.email || 'N/A'}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(version);
                            }}
                          >
                            <Icon>delete</Icon>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <TableContainer component={Paper}>
      <Table aria-label="custom object expandable table">
        <TableBody>
          {data.map((group) => (
            <React.Fragment key={group.name}>
              <StyledTableRow onClick={() => handleRowClick(group.name)}>
                <TableCell>
                  <ArgonTypography variant="subtitle2" fontWeight="medium">
                    {group.name}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="text">
                    {group.versions.length} version(s)
                  </ArgonTypography>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleRowClick(group.name)}>
                    <Icon>{expandedRow === group.name ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                  </IconButton>
                </TableCell>
              </StyledTableRow>
              {renderRowSubComponent(group)}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

CustomObjectExpandableTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      versions: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string.isRequired,
          version: PropTypes.number.isRequired,
          is_latest: PropTypes.bool,
          created_at: PropTypes.string.isRequired,
          user: PropTypes.shape({
            email: PropTypes.string,
          }),
        })
      ).isRequired,
    })
  ).isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default CustomObjectExpandableTable;
